import React, { useState, useEffect } from 'react';
import { SessionConfig, StepType, StepIntent } from '../../types';
import {
  Settings as SettingsIcon,
  Lock,
  RefreshCw,
  MapPin,
  Info,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from 'lucide-react';

interface SessionConfigurationProps {
  config?: SessionConfig;
  onChange: (config: SessionConfig) => void;
  stepType: StepType;
  intent?: StepIntent;
}

const SessionConfiguration: React.FC<SessionConfigurationProps> = ({
  config,
  onChange,
  stepType,
  intent
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [localConfig, setLocalConfig] = useState<SessionConfig>(() =>
    config || getDefaultConfig(stepType, intent)
  );

  useEffect(() => {
    if (config) {
      setLocalConfig(config);
    }
  }, [config]);

  // Notify parent of initial default config on mount
  useEffect(() => {
    if (!config) {
      const defaultConfig = getDefaultConfig(stepType, intent);
      onChange(defaultConfig);
    }
  }, []); // Empty dependency array = run only on mount

  // Update session mode when intent changes
  useEffect(() => {
    if (stepType === 'run_agent' && intent) {
      const recommendedMode = intent === 'assistant' ? 'shared' : 'isolated';
      if (localConfig.mode !== recommendedMode) {
        handleConfigChange({ mode: recommendedMode, enabled: true });
      }
    }
  }, [intent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Smart defaults based on step type and intent
  function getDefaultConfig(stepType: StepType, intent?: StepIntent): SessionConfig {
    if (stepType === 'run_agent') {
      if (intent === 'assistant') {
        // Assistant intent: shared memory across runs for conversation continuity
        return {
          enabled: true,
          mode: 'shared',
          type: 'conversation',
          ttl_minutes: 60,
          max_messages: 1000
        };
      }
      // Create/enhance/validate intents: isolated, each run starts fresh
      return {
        enabled: true,
        mode: 'isolated',
        type: 'conversation',
        ttl_minutes: 60,
        max_messages: 1000
      };
    }
    return {
      enabled: false,
      mode: 'isolated',
      type: 'conversation',
      ttl_minutes: 30,
      max_messages: 500
    };
  }

  const handleConfigChange = (updates: Partial<SessionConfig>) => {
    const newConfig = { ...localConfig, ...updates };
    setLocalConfig(newConfig);
    onChange(newConfig);
  };

  const modeOptions = [
    {
      value: 'isolated',
      label: 'Isolated',
      description: 'Each run starts fresh. No memory between runs.',
      icon: <Lock className="w-4 h-4" />,
    },
    {
      value: 'shared',
      label: 'Shared',
      description: 'All runs share memory. Perfect for assistants.',
      icon: <RefreshCw className="w-4 h-4" />,
    },
    {
      value: 'run',
      label: 'Run-scoped',
      description: 'Memory within a single run instance only.',
      icon: <MapPin className="w-4 h-4" />,
    },
  ];

  const isAssistant = intent === 'assistant';

  return (
    <div className="w-full p-2">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-4 flex items-center">
        <SettingsIcon className="w-5 h-5 mr-2" />
        Session Settings
      </h3>

      {/* Enable Session Toggle */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Enable Session Memory
            </label>
            <div className="relative group ml-2">
              <Info className="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-help" />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                {isAssistant
                  ? 'Required for assistant intent to maintain conversation context'
                  : 'Store conversation context for this agent'}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => !isAssistant && handleConfigChange({ enabled: !localConfig.enabled })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              localConfig.enabled
                ? 'bg-primary-600'
                : 'bg-gray-300 dark:bg-gray-600'
            } ${isAssistant ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                localConfig.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        {isAssistant && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Session memory is required for assistant steps.
          </p>
        )}
      </div>

      {localConfig.enabled && (
        <>
          {/* Session Mode */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Session Mode
            </label>
            {isAssistant ? (
              /* Locked to shared for assistant intent */
              <div className="flex items-center p-3 rounded-lg border border-primary-500 bg-primary-50 dark:bg-primary-900/20">
                <span className="mr-3 text-primary-600 dark:text-primary-400">
                  <RefreshCw className="w-4 h-4" />
                </span>
                <div className="flex-1">
                  <div className="text-sm font-medium text-primary-700 dark:text-primary-300">
                    Shared
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Required for assistants to maintain conversation context across runs.
                  </div>
                </div>
                <Lock className="w-4 h-4 text-gray-400 dark:text-gray-500 ml-2" />
              </div>
            ) : (
              /* Selectable mode options for non-assistant intents */
              <div className="space-y-2">
                {modeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleConfigChange({ mode: option.value as 'isolated' | 'shared' | 'run' })}
                    className={`w-full flex items-center p-3 rounded-lg border text-left transition-colors ${
                      localConfig.mode === option.value
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className={`mr-3 ${
                      localConfig.mode === option.value
                        ? 'text-primary-600 dark:text-primary-400'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {option.icon}
                    </span>
                    <div>
                      <div className={`text-sm font-medium ${
                        localConfig.mode === option.value
                          ? 'text-primary-700 dark:text-primary-300'
                          : 'text-gray-900 dark:text-warm-white'
                      }`}>
                        {option.label}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {option.description}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Advanced Settings */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-2 transition-colors"
          >
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            <span className="text-sm ml-1">Advanced Settings</span>
          </button>

          {showAdvanced && (
            <div className="pl-2 pr-2 pt-2 space-y-4">
              {/* TTL */}
              <div>
                <div className="flex items-center mb-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Session TTL (minutes)
                  </label>
                  <div className="relative group ml-2">
                    <Info className="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-help" />
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                      How long unused sessions stay active. Longer = more memory usage.
                    </div>
                  </div>
                </div>
                <input
                  type="number"
                  value={localConfig.ttl_minutes || 60}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= 1 && value <= 10080) {
                      handleConfigChange({ ttl_minutes: value });
                    }
                  }}
                  min={1}
                  max={10080}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Between 1 minute and 1 week (10080 minutes)
                </p>
              </div>

              {/* Max Messages */}
              <div>
                <div className="flex items-center mb-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Max Messages
                  </label>
                  <div className="relative group ml-2">
                    <Info className="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-help" />
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                      Maximum number of messages to store in session history
                    </div>
                  </div>
                </div>
                <input
                  type="number"
                  value={localConfig.max_messages || 1000}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= 10 && value <= 10000) {
                      handleConfigChange({ max_messages: value });
                    }
                  }}
                  min={10}
                  max={10000}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Between 10 and 10000 messages
                </p>
              </div>
            </div>
          )}

          {/* Mode Change Warning */}
          {config && config.mode !== localConfig.mode && (
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 mr-2 flex-shrink-0" />
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Changing session mode will affect how context is preserved.
                {config.mode === 'shared' && localConfig.mode === 'isolated' &&
                  ' Switching from Shared to Isolated will prevent access to existing session history.'}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SessionConfiguration;
